import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import {
  type AppointmentEventType,
  LanguageUtils,
  LinkButton,
  moment,
  type Nil,
  PermissionArea,
  Utils,
} from '@pbt/pbt-ui-components'

import type { PreviousMedicalRecord } from '~/api/graphql/generated/types'
import { AppointmentHistoryLabel } from '~/components/common/labels/AppointmentHistoryLabel'
import ItemHistoryLabel from '~/components/common/labels/ItemHistoryLabel'
import Link from '~/components/common/link/Link'
import { AppointmentTypeName } from '~/constants/appointmentTypes'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import { useOpenInvoice } from '~/store/hooks/finance'
import { getCRUDByArea } from '~/store/reducers/auth'
import { getEventType, getFeatureToggle } from '~/store/reducers/constants'
import type {
  AppointmentTimelineItem,
  AppointmentTimelineItemEntity,
} from '~/types'
import { addOriginalBusinessId } from '~/utils'
import { usePersistClickedItemOnURL } from '~/utils/routes'
import {
  getLogIdFromTimelineAppoitment,
  getLogTypeFromTimelineAppoitment,
} from '~/utils/timeline'
import useDialog from '~/utils/useDialog'
import useIsCurrentContextItem from '~/utils/useIsCurrentContextItem'

import TimelineCard, { type TimelineCardProps } from '../TimelineCard'
import TimelineCardActions from '../TimelineCardActions'
import TimelineCardContent from '../TimelineCardContent'
import MultipleSoapAppointment from './MultipleSoapAppointment'
import SingleSoapAppointment from './SingleSoapAppointment'
import TimelinePreviousMedicalRecords from './TimelinePreviousMedicalRecords'

const useStyles = makeStyles(
  (theme) => ({
    actionsRoot: {
      paddingBottom: 0,
      minHeight: theme.spacing(5),
    },
  }),
  { name: 'AppointmentCard' },
)

export interface AppointmentCardProps extends TimelineCardProps {
  clientId: string
  index: number
  item: AppointmentTimelineItem
  patientId: string
  previousMedicalRecord?: PreviousMedicalRecord | null
}

const timeBuffer = 2

const AppointmentCard = ({
  item,
  clientId,
  patientId,
  index,
  previousMedicalRecord,
  ...rest
}: AppointmentCardProps) => {
  const classes = useStyles()

  const { t } = useTranslation('Common')

  const EventType = useSelector(getEventType)
  const soapPermissions = useSelector(getCRUDByArea(PermissionArea.SOAP))
  const invoicePermissions = useSelector(getCRUDByArea(PermissionArea.INVOICE))
  const isPatientSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PATIENT_SHARING),
  )
  const isAppointmentCancellationReasonEnabled = useSelector(
    getFeatureToggle(FeatureToggle.APPOINTMENT_CANCELLATION_REASON),
  )
  const isPmrConsentEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PMR_NEW_APPOINTMENT),
  )

  const [openInvoiceDialog] = useDialog(DialogNames.INVOICE)
  const openInvoice = useOpenInvoice(clientId, openInvoiceDialog)
  const persistClickedItem = usePersistClickedItemOnURL()

  const onViewInvoiceClick = useCallback(() => {
    const logId = getLogIdFromTimelineAppoitment(item) as unknown as
      | string
      | Nil
    const logType = getLogTypeFromTimelineAppoitment(item) as unknown as
      | string
      | Nil
    persistClickedItem(index)
    openInvoice({
      clientId,
      patientId,
      invoiceId: item.invoiceId,
      soapId: R.pipe(R.prop('entries'), R.head, R.prop('soapId'))(item),
      logId,
      logType,
    })
  }, [clientId, patientId, item.invoiceId])

  const hasOneSoap = item.entries?.length === 1 && item.entries[0]?.soapId
  const hasMultipleSoaps = item.entries?.length > 1
  const hasNoSoap = !hasOneSoap && !hasMultipleSoaps

  const isContextItem = useIsCurrentContextItem(item)
  const hasSoap = R.pipe(
    R.prop<AppointmentTimelineItemEntity[]>('entries'),
    R.any(R.pipe(R.prop('soapId'), R.complement(R.either(R.isNil, R.isEmpty)))),
  )(item)
  const hasInvoice = Boolean(item.invoiceId)
  const isPastAppointment = moment().isAfter(item.date, 'days')

  const AppointmentEvent: AppointmentEventType = Utils.findConstantByName(
    'Appointment',
    EventType,
  )

  const appointmentTypeColor = item.businessAppointmentType?.color

  const title = item.businessAppointmentType?.name

  const appointmentState =
    AppointmentEvent && Utils.findById(item.state, AppointmentEvent.states)

  const originalBusinessId = isPatientSharingEnabled ? item.businessId : null
  const showCreateSoapLink =
    hasNoSoap &&
    !isPastAppointment &&
    soapPermissions.create &&
    item.appointmentTypeName !== AppointmentTypeName.OTC_SALE &&
    isContextItem

  // Only show PMR if it was created within a small time window around this appointment creation date
  // (from creationDate - timeBuffer minutes to creationDate + timeBuffer minutes), to avoid showing PMR from unrelated previous appointments
  const shouldShowPmr = Boolean(
    isPmrConsentEnabled &&
    previousMedicalRecord?.createdAt &&
    moment(previousMedicalRecord.createdAt).isSameOrAfter(
      moment(item.creationDate).subtract(timeBuffer, 'minutes'),
    ) &&
    moment(previousMedicalRecord.createdAt).isSameOrBefore(
      moment(item.creationDate).add(timeBuffer, 'minutes'),
    ),
  )

  return (
    <TimelineCard
      doubleMargin={hasMultipleSoaps}
      originBusinessId={item.businessId}
      title={title}
      titlePinColor={appointmentTypeColor}
      {...item}
      {...rest}
    >
      <TimelineCardContent noTypography noPadding={hasMultipleSoaps}>
        {hasMultipleSoaps ? (
          <MultipleSoapAppointment
            striped
            clientId={clientId}
            item={item}
            openInvoice={openInvoice}
            patientId={patientId}
          />
        ) : (
          <SingleSoapAppointment
            clientId={clientId}
            item={item}
            patientId={patientId}
          />
        )}
      </TimelineCardContent>
      {shouldShowPmr && (
        <TimelinePreviousMedicalRecords
          previousMedicalRecord={previousMedicalRecord}
        />
      )}
      <TimelineCardActions
        classes={{
          root: classes.actionsRoot,
        }}
        state={
          appointmentState &&
          LanguageUtils.getTranslatedFieldName(appointmentState)
        }
      >
        {(hasInvoice || hasSoap) &&
          invoicePermissions.read &&
          isContextItem &&
          !hasMultipleSoaps && (
            <LinkButton onClick={onViewInvoiceClick}>
              {t('Common:VIEW_INVOICE')}
            </LinkButton>
          )}
        {showCreateSoapLink && (
          <Link
            to={addOriginalBusinessId(
              `/soap/create/${item.id}`,
              originalBusinessId,
            )}
            onClick={() => persistClickedItem(index)}
          >
            {t('Common:CREATE_SOAP')}
          </Link>
        )}
        {hasMultipleSoaps &&
          !isPastAppointment &&
          soapPermissions.create &&
          isContextItem && (
            <Link
              to={addOriginalBusinessId(
                `/soap/add/${item.id}`,
                originalBusinessId,
              )}
              onClick={() => persistClickedItem(index)}
            >
              {t('Common:ADD_SOAP')}
            </Link>
          )}
        {hasOneSoap && soapPermissions.read && (
          <Link
            to={addOriginalBusinessId(
              `/soap/${item.entries[0].soapId}`,
              originalBusinessId,
            )}
            onClick={() => persistClickedItem(index)}
          >
            {t('Common:VIEW_SOAP')}
          </Link>
        )}
      </TimelineCardActions>
      <Grid container pb={1.5} px={2}>
        {isAppointmentCancellationReasonEnabled ? (
          <AppointmentHistoryLabel
            item={{
              ...item,
              cancellationReason: item.appointmentCancellationReason,
              cancellationReasonId: item.appointmentCancellationReasonId,
              stateId: item.state,
            }}
          />
        ) : (
          <ItemHistoryLabel item={item} />
        )}
      </Grid>
    </TimelineCard>
  )
}

export default AppointmentCard
